// Central content + configuration for the StUpEco Workspace marketing site.

export const LOGO_URL =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_a6dab118-47f2-410a-a153-59c683badcce/artifacts/2aodl2mo_StUpEco%20TP.png";

export const NAV_LINKS = [
  { label: "Product", to: "/product" },
  { label: "Features", to: "/features" },
  { label: "Solutions", to: "/solutions" },
  { label: "Pricing", to: "/pricing" },
  { label: "Security", to: "/security" },
  { label: "Resources", to: "/resources" },
];

// Product overview pillars
export const PILLARS = [
  {
    key: "plan",
    title: "Plan",
    desc: "Shape strategy, set goals and map the work before it begins — one source of truth for where the business is heading.",
    icon: "Target",
  },
  {
    key: "operate",
    title: "Operate",
    desc: "Run day-to-day operations — customers, tasks and pipelines — without switching between ten disconnected tools.",
    icon: "Workflow",
  },
  {
    key: "collaborate",
    title: "Collaborate",
    desc: "Bring your team, documents and conversations together so context never gets lost between apps.",
    icon: "Users",
  },
  {
    key: "grow",
    title: "Grow",
    desc: "Turn operational data into insight with reports and AI, so every decision compounds into growth.",
    icon: "TrendingUp",
  },
];

export const STATUS = {
  live: { label: "Live", tone: "live" },
  soon: { label: "Available Soon", tone: "soon" },
  dev: { label: "In Development", tone: "dev" },
  future: { label: "Future", tone: "future" },
};

// Live modules today — Phase 12.7 is shipped.
export const MODULES = [
  { name: "Command Center", icon: "LayoutDashboard", status: "live", desc: "Run the day: today's priorities, overdue items, deals needing action, pipeline health — all in one place.", span: "lg" },
  { name: "Board", icon: "Landmark", status: "live", desc: "Think about the business. A leadership team of specialist perspectives grounded in your real data.", span: "md" },
  { name: "Goals", icon: "Target", status: "live", desc: "Set strategic goals with roadmaps drafted from your business context.", span: "sm" },
  { name: "Work", icon: "CheckSquare", status: "live", desc: "Prioritise and track the tasks that move things forward.", span: "sm" },
  { name: "Sales / CRM", icon: "Contact", status: "live", desc: "Companies, contacts, deals and pipelines in one connected record.", span: "md" },
  { name: "Projects", icon: "FolderKanban", status: "live", desc: "Plan initiatives with milestones, tasks, and progress rolling up automatically.", span: "sm" },
  { name: "Calendar", icon: "Calendar", status: "live", desc: "Schedule and see commitments across the workspace.", span: "sm" },
  { name: "Meeting Assist", icon: "NotebookPen", status: "live", desc: "Capture meetings, get action items, and keep the record connected to CRM and projects.", span: "sm" },
  { name: "Business Data", icon: "Database", status: "live", desc: "Custom trackers for the things that don't fit CRM or Projects — vendors, assets, campaigns, candidates and more.", span: "md" },
  { name: "Connected Intelligence", icon: "Sparkles", status: "live", desc: "Every module talks to a shared business memory, so briefings, drafts and recommendations are grounded in your reality.", span: "lg" },
];

// Why Workspace — numbered manifesto chapters
export const MANIFESTO = [
  {
    n: "01",
    title: "The average founder runs on a dozen disconnected tools.",
    body: "A CRM here, a task app there, docs in a fourth place. Context lives everywhere and nowhere. The tools were supposed to help — instead they fragment the business.",
  },
  {
    n: "02",
    title: "Every switch costs momentum.",
    body: "Copy-pasting between apps, re-explaining context, hunting for the latest version. The busywork of holding it all together quietly eats the day.",
  },
  {
    n: "03",
    title: "Disconnected data means blind decisions.",
    body: "When customers, work and knowledge don't talk to each other, you can't see the full picture — so you guess instead of knowing.",
  },
  {
    n: "04",
    title: "Workspace connects it into one system.",
    body: "One place where operations, people and knowledge live together — with AI on top. Less tool-tax, more building the business.",
  },
];

// Built For personas
export const PERSONAS = [
  { title: "Solo Founders", desc: "Run everything from one place without a stack of subscriptions.", icon: "User" },
  { title: "Startups", desc: "Move fast with a shared operating system as you scale the team.", icon: "Rocket" },
  { title: "Agencies", desc: "Manage clients, projects and delivery without the chaos.", icon: "Briefcase" },
  { title: "Consultants", desc: "Keep engagements, knowledge and follow-ups perfectly organised.", icon: "Lightbulb" },
  { title: "Small Businesses", desc: "Operate the whole business from a single connected workspace.", icon: "Store" },
];

export const CURRENCIES = [
  { code: "INR", symbol: "₹", flag: "🇮🇳" },
  { code: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "AED", symbol: "AED ", flag: "🇦🇪" },
  { code: "EUR", symbol: "€", flag: "🇪🇺" },
];

// Launch pricing (limited-time) vs regular pricing, per currency. Monthly.
// annualDiscountMonths: buy annually -> pay for (12 - annualDiscountMonths) months (Save 2 months by default).
export const ANNUAL_MONTHS_FREE = 2;

export const PRICING_PLANS = [
  {
    id: "starter",
    name: "Starter",
    tagline: "For founders getting started",
    highlight: false,
    prices: {
      INR: "Free", USD: "Free", AED: "Free", EUR: "Free",
    },
    outcomes: [
      "Run your business from one connected workspace",
      "Business Briefing every morning",
      "Business Assistant — plan and act with guidance",
      "Business memory that keeps context",
    ],
    cta: "Get Started Free", ctaTo: "/signup", launched: true,
  },
  {
    id: "growth",
    name: "Growth",
    tagline: "For founders ready to scale",
    highlight: true,
    prices: {
      INR: "999", USD: "12", AED: "44", EUR: "11",
    },
    outcomes: [
      "Everything in Starter",
      "Board · Leadership Team advisory",
      "Business Data — custom trackers",
      "Priority support",
    ],
    cta: "Choose Growth", plan: "growth", launched: true,
  },
  {
    id: "business",
    name: "Business",
    tagline: "For teams operating at pace",
    highlight: false,
    prices: {
      INR: "2,999", USD: "36", AED: "132", EUR: "33",
    },
    outcomes: [
      "Everything in Growth",
      "Team roles, members & permissions",
      "Shared business context across the team",
      "Advanced controls & reporting",
    ],
    cta: "Choose Business", plan: "business", launched: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For scaled operations",
    highlight: false,
    prices: "contact",
    outcomes: [
      "Everything in Business",
      "Custom onboarding & success",
      "Security & compliance reviews",
      "Dedicated partnership",
    ],
    cta: "Contact Us", ctaTo: "/contact", launched: true,
  },
];

export const FAQS = [
  { q: "What is StUpEco Workspace?", a: "It's an AI-powered business operating system that brings customers, work, knowledge and operations into one connected platform — instead of a dozen disconnected tools." },
  { q: "Is StUpEco Workspace available today?", a: "Yes. You can create your workspace now, complete a short setup, and start working with your AI business partner immediately. New connected capabilities are added continuously." },
  { q: "How much does it cost?", a: "StUpEco Workspace offers Starter, Growth, Business and Enterprise plans. Early adopters get special launch pricing — Starter is free, with Growth and Business at a limited-time discount. See the Pricing page for current rates in your currency." },
  { q: "Who is Workspace for?", a: "Solo founders, startups, agencies, consultants and small businesses who want to run and grow their whole business from one connected, AI-powered workspace." },
  { q: "Can I start now?", a: "Yes — create a free account in seconds. You'll be guided through a short setup so Workspace and its AI understand your business from day one." },
  { q: "Do you support light and dark mode?", a: "Yes. The experience follows your system preference automatically and you can switch manually anytime — your choice is remembered." },
  { q: "How is my data handled?", a: "Privacy-first by default. Traffic is encrypted in transit, passwords are hashed, and your business data is never sold. See our Security & Privacy page for details." },
  { q: "What makes the AI different?", a: "The AI is the operating layer of the whole platform — grounded in your business context, it acts as your executive assistant, COO and advisor, helping you decide and act rather than just chat." },
];

export const TESTIMONIALS = [
  { quote: "Finally, one place instead of ten tabs. This is how running a business should feel.", name: "Amara O.", role: "Solo Founder" },
  { quote: "The idea of one connected system is exactly what our agency has been missing for years.", name: "Daniel R.", role: "Agency Owner" },
  { quote: "Less time wrangling tools, more time with clients. It feels like having an operations team.", name: "Priya S.", role: "Independent Consultant" },
];

export const ROADMAP = [
  { phase: "Operate", title: "Run the business", status: "Live", items: ["Command Center", "AI briefing", "Business memory"] },
  { phase: "Grow", title: "Connected capabilities", status: "Expanding", items: ["Customers & sales", "Work & projects", "Knowledge & docs"] },
];

export const FOOTER = {
  Product: [
    { label: "Overview", to: "/product" },
    { label: "Features", to: "/features" },
    { label: "Solutions", to: "/solutions" },
    { label: "Pricing", to: "/pricing" },
    { label: "Security", to: "/security" },
  ],
  Company: [
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
    { label: "Support", to: "/contact" },
  ],
  Resources: [
    { label: "Resources", to: "/resources" },
    { label: "Blog", to: "/resources" },
    { label: "FAQ", to: "/faq" },
    { label: "Start Free", to: "/signup" },
    { label: "Login", to: "/login" },
  ],
  Legal: [
    { label: "Privacy", to: "/privacy" },
    { label: "Terms", to: "/terms" },
    { label: "Intellectual Property", to: "/legal/intellectual-property" },
    { label: "Security", to: "/security" },
  ],
};

export const RESOURCES = [
  { title: "Guides", desc: "Practical playbooks on running a connected business.", tag: "Guides", icon: "BookOpen" },
  { title: "Blog", desc: "Founder stories and ideas on operating and growing.", tag: "Articles", icon: "Newspaper" },
  { title: "Help Center", desc: "Answers, docs and onboarding for Workspace.", tag: "Support", icon: "LifeBuoy" },
  { title: "Customer Stories", desc: "How founders run their business on Workspace.", tag: "Stories", icon: "Quote" },
];

// Comparison: disconnected tools vs one Workspace
export const COMPARISON = {
  disconnected: [
    "A dozen logins, tabs and subscriptions",
    "Data scattered across apps that don't talk",
    "Context lost between hand-offs",
    "Manual copy-paste busywork",
    "Blind decisions from siloed data",
    "AI that only sees one corner",
  ],
  workspace: [
    "One login, one connected workspace",
    "A single source of truth for everything",
    "Context that follows the work",
    "Automations instead of busywork",
    "Decisions backed by the full picture",
    "AI that understands your whole operation",
  ],
};

// AI capabilities
export const AI_CAPS = [
  { icon: "Sparkles", title: "Understands your business", desc: "Grounded in your customers, projects and docs — not generic answers." },
  { icon: "Wand2", title: "Drafts and automates", desc: "Turn a sentence into tasks, replies, summaries and follow-ups." },
  { icon: "Search", title: "Finds anything instantly", desc: "Ask in plain language across every module and get the answer." },
  { icon: "LineChart", title: "Surfaces what matters", desc: "Proactive nudges and insights so nothing slips through." },
];

// Integrations (logos as names — no fake claims of availability)
export const INTEGRATIONS = [
  "Gmail", "Google Calendar", "Slack", "Notion", "Stripe", "Zapier",
  "Outlook", "Zoom", "HubSpot", "QuickBooks", "Drive", "Trello",
];

// Trust & security pillars
export const TRUST = [
  { icon: "ShieldCheck", title: "Privacy-first by default", desc: "Your data is yours. Clear ownership and sensible defaults from day one." },
  { icon: "Lock", title: "Encryption in transit", desc: "Traffic is protected with modern TLS across the platform." },
  { icon: "KeyRound", title: "Secure authentication", desc: "Hardened sessions, hashed passwords and trusted social login." },
  { icon: "Server", title: "Reliable foundations", desc: "Built on a robust, scalable stack designed to grow with you." },
];
